import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

type CookieConsentState = {
  isAccepted: boolean;
  isDeclined: boolean;
  hasHydrated: boolean;
  setIsAccepted: (accepted: boolean) => void;
  setIsDeclined: (declined: boolean) => void;
  setHasHydrated: (hydrated: boolean) => void;
};

const COOKIE_NAME = 'analytics-consent';
const COOKIE_MAX_AGE = 365 * 24 * 60 * 60; // 1 year in seconds

const setConsentCookie = (value: 'accepted' | 'declined') => {
  if (typeof document !== 'undefined') {
    document.cookie = `${COOKIE_NAME}=${value}; path=/; max-age=${COOKIE_MAX_AGE}; SameSite=Strict`;
  }
};

const removeConsentCookie = () => {
  if (typeof document !== 'undefined') {
    document.cookie = `${COOKIE_NAME}=; path=/; max-age=0; SameSite=Strict`;
  }
};

export const useCookieConsent = create(
  persist<CookieConsentState>(
    (set) => ({
      isAccepted: false,
      isDeclined: false,
      hasHydrated: false,
      setIsAccepted: (accepted: boolean) => {
        if (accepted) {
          setConsentCookie('accepted');
          set({ isAccepted: true, isDeclined: false });
        } else {
          removeConsentCookie();
          set({ isAccepted: false });
        }
      },
      setIsDeclined: (declined: boolean) => {
        if (declined) {
          setConsentCookie('declined');
          set({ isDeclined: true, isAccepted: false });
        } else {
          removeConsentCookie();
          set({ isDeclined: false });
        }
      },
      setHasHydrated: (hydrated: boolean) => set({ hasHydrated: hydrated }),
    }),
    {
      name: 'cookie-consent',
      version: 2,
      storage: createJSONStorage(() => localStorage),
      onRehydrateStorage: () => (state) => {
        state?.setHasHydrated(true);
      },
    }
  )
);
